/**
 * @file api.ts
 * @brief Company accounts and deletion (BACKEND B7.2), web + Mac.
 *
 * @details
 *   useMyDeletionRequest   → GET  /api/auth/account/deletion-request
 *   useRequestDeletion     → POST /api/auth/account/deletion-request { reason? }
 *   useCompanyDeletion     → GET  /api/company/deletion (company admin)
 *   useDeleteCompany       → DELETE /api/company { confirm, currentPassword,
 *                            twoFactorCode? | backupCode? (B33) }
 * A successful company delete revokes every token, the caller's too: show
 * the message, then sign out locally.
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@/shared/api/client";
import { authEndpoints, companyEndpoints } from "@/shared/api/endpoints";
import { parseCompanyDeletionInfo, parseDeletionRequest, type DeletionRequest } from "./accountDeletion";

export const myDeletionRequestKey = ["auth", "deletion-request"] as const;
export const companyDeletionKey = ["company", "deletion"] as const;

export function useMyDeletionRequest(enabled: boolean) {
  return useQuery({
    queryKey: myDeletionRequestKey,
    enabled,
    retry: false,
    queryFn: async () =>
      parseDeletionRequest((await apiGet<{ request?: unknown }>(authEndpoints.deletionRequest))?.request),
  });
}

export function useRequestDeletion() {
  const qc = useQueryClient();
  return useMutation<{ message: string; request: DeletionRequest | null }, Error, { reason?: string }>({
    mutationFn: async ({ reason }) => {
      const res = await apiPost<{ message?: string; request?: unknown }>(
        authEndpoints.deletionRequest,
        reason?.trim() ? { reason: reason.trim().slice(0, 500) } : {},
      );
      return {
        message:
          res?.message ||
          "We've sent your request to your company administrator and VERBLIZR support. Your account will be deleted within 30 days.",
        request: parseDeletionRequest(res?.request),
      };
    },
    onSuccess: (done) => qc.setQueryData(myDeletionRequestKey, done.request),
  });
}

export function useCompanyDeletion(enabled: boolean) {
  return useQuery({
    queryKey: companyDeletionKey,
    enabled,
    retry: false,
    queryFn: async () => parseCompanyDeletionInfo(await apiGet<unknown>(companyEndpoints.deletion)),
  });
}

export function useDeleteCompany() {
  return useMutation<
    { message: string; erasureDueAt: string | null },
    Error,
    { confirm: string; currentPassword: string; twoFactorCode?: string; backupCode?: string }
  >({
    mutationFn: async (input) => {
      const res = await apiDelete<{ message?: string; erasureDueAt?: string | null }>(companyEndpoints.company, {
        data: input,
      });
      return {
        message:
          res?.message ||
          "We've started deleting your company. Everyone has been signed out, the plan is cancelled, and all data will be gone within 30 days.",
        erasureDueAt: res?.erasureDueAt ?? null,
      };
    },
  });
}
